import { useMemo } from 'react';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { getKeybindingLayoutMap } from '../dispatch/effectiveKeymap';
import { sameChordOnLayout } from '../model/chordIdentity';
import type { LayoutMapCache } from '../model/layoutMap';
import { platform as currentPlatform } from '../model/platform';
import type { KeybindingCommandId, Platform } from '../model/keybindingTypes';
import { useKeybindingsStore } from '../store/keybindingsStore';
import { canReclaim } from '../store/keybindingsSelectors';
import type { KeybindingsData } from '../store/keybindingsStoreTypes';
import { isShownWarning } from './keybindingDisplay';

/** Which chip the recorder is replacing, or a new chord being added. */
export type RecordingMode =
  | { readonly kind: 'replace'; readonly chord: string }
  | { readonly kind: 'add' };

export type ActiveRecording = {
  readonly commandId: KeybindingCommandId;
  readonly mode: RecordingMode;
};

type ChangeOutcome = 'bound' | 'cleared' | 'cancelled' | 'rejected';

/** The command's chords after the recorder committed `chord` in `mode`. */
export function chordsAfterRecording(
  current: readonly string[],
  mode: RecordingMode,
  chord: string,
): string[] {
  if (mode.kind === 'add') return [...new Set([...current, chord])];
  const index = current.indexOf(mode.chord);
  const next = index === -1 ? [...current, chord] : current.map((item, i) => (i === index ? chord : item));
  return [...new Set(next)];
}

/**
 * `hasWarning`: the change left a warning the tab shows on this command (its
 * own row or a scoped-vs-global pair it belongs to). `displacedOther`: it took
 * a chord from another command in the same scope (a durable collision notice).
 */
export function changeFlags(
  state: Pick<KeybindingsData, 'notices' | 'scheme'>,
  commandId: KeybindingCommandId,
  targetPlatform: Platform = currentPlatform(),
): { hasWarning: boolean; displacedOther: boolean } {
  return {
    hasWarning: state.notices.some((notice) =>
      !notice.persisted && (notice.commandId === commandId || notice.relatedCommandId === commandId) &&
      isShownWarning(notice, state.scheme, targetPlatform)),
    displacedOther: state.notices.some((notice) =>
      notice.kind === 'collision' && notice.persisted && notice.relatedCommandId === commandId),
  };
}

export type KeybindingEditActions = {
  readonly commit: (recording: ActiveRecording, current: readonly string[], chord: string) => void;
  readonly clear: (recording: ActiveRecording) => void;
  readonly cancel: (commandId: KeybindingCommandId) => void;
  readonly reject: (commandId: KeybindingCommandId) => void;
  readonly reset: (commandId: KeybindingCommandId) => void;
};

/**
 * Store writes + `feature:keybinding_changed` / `feature:keybinding_reset`
 * analytics for the Settings tab. Properties are the command id (a closed set)
 * and booleans — never a chord.
 */
export function useKeybindingEditActions(
  targetPlatform: Platform = currentPlatform(),
  layoutMap: Pick<LayoutMapCache, 'getSnapshot'> = getKeybindingLayoutMap(),
): KeybindingEditActions {
  const { track } = useAnalytics();
  return useMemo(() => {
    const report = (commandId: KeybindingCommandId, outcome: ChangeOutcome) => {
      const flags = outcome === 'bound' || outcome === 'cleared'
        ? changeFlags(useKeybindingsStore.getState(), commandId)
        : { hasWarning: false, displacedOther: false };
      track('feature:keybinding_changed', { commandId, outcome, ...flags });
    };
    return {
      commit: ({ commandId, mode }, current, chord) => {
        const store = useKeybindingsStore.getState();
        // Identity, not spelling: Ctrl+Shift+S is Mod+Shift+S off macOS, and a
        // physical Ctrl+[Backquote] is Ctrl+` on a US layout.
        const same = (stored: string) => sameChordOnLayout(stored, chord, targetPlatform, layoutMap.getSnapshot());
        if (mode.kind === 'replace' && same(mode.chord)) {
          // The chip's own keys: nothing changes — unless the chip lost a
          // conflict with another command; then recording it again makes it win.
          // (A layout overlap, a platform reserve or a same-row duplicate cannot be won.)
          // claimBinding also refuses when the chord would stay inactive once settled.
          const claimed = canReclaim(store, commandId, mode.chord) && store.claimBinding(commandId, mode.chord);
          report(commandId, claimed ? 'bound' : 'cancelled');
          return;
        }
        if (mode.kind === 'replace') {
          // Keys another chip of the row already has: the row loses the replaced
          // chip, and that other chip wins its conflict if it had lost one.
          const other = current.find((stored) => stored !== mode.chord && same(stored));
          const claimed = other !== undefined && canReclaim(store, commandId, other) &&
            store.claimBinding(commandId, other, mode.chord);
          if (!claimed) {
            store.setBinding(commandId, other !== undefined
              ? current.filter((stored) => stored !== mode.chord)
              : chordsAfterRecording(current, mode, chord));
          }
          report(commandId, 'bound');
          return;
        }
        // Adding keys the row already holds keeps the stored spelling: no write, no new chip.
        if (current.some(same)) {
          report(commandId, 'cancelled');
          return;
        }
        store.addBinding(commandId, chord);
        report(commandId, 'bound');
      },
      clear: ({ commandId, mode }) => {
        // Backspace clears the chip being re-recorded; with nothing to clear
        // (a new chord being added) it just ends the recording.
        if (mode.kind === 'add') {
          report(commandId, 'cancelled');
          return;
        }
        useKeybindingsStore.getState().removeBinding(commandId, mode.chord);
        report(commandId, 'cleared');
      },
      cancel: (commandId) => report(commandId, 'cancelled'),
      reject: (commandId) => report(commandId, 'rejected'),
      reset: (commandId) => {
        useKeybindingsStore.getState().resetCommand(commandId);
        track('feature:keybinding_reset', { commandId });
      },
    };
  }, [layoutMap, targetPlatform, track]);
}
